import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BottomSheet } from './BottomSheet';
import { useThemeStore } from '../../store/themeStore';
import { typography } from '../../utils/typography';
import { spacing } from '../../utils/theme';

export interface ActionSheetOption {
  label: string;
  icon?: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface ActionSheetProps {
  visible: boolean;
  onClose: () => void;
  options: ActionSheetOption[];
  title?: string;
}

export const ActionSheet: React.FC<ActionSheetProps> = ({
  visible,
  onClose,
  options,
  title,
}) => {
  const { colors } = useThemeStore();
  const styles = createStyles(colors);
  
  const handleOptionPress = (option: ActionSheetOption) => {
    if (!option.disabled) {
      option.onPress();
      onClose();
    }
  };

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.container}>
        {title && (
          <View style={styles.titleContainer}>
            <Text style={styles.title}>{title}</Text>
          </View>
        )}
        
        <View style={styles.optionsContainer}>
          {options.map((option, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.option,
                option.disabled && styles.optionDisabled,
              ]}
              onPress={() => handleOptionPress(option)}
              disabled={option.disabled}
              activeOpacity={0.7}
            >
              {option.icon && (
                <Ionicons
                  name={option.icon}
                  size={24}
                  color={
                    option.disabled
                      ? colors.textSecondary
                      : option.destructive
                      ? colors.error
                      : colors.text
                  }
                  style={styles.icon}
                />
              )}
              <Text
                style={[
                  styles.optionLabel,
                  option.destructive && styles.optionLabelDestructive,
                  option.disabled && styles.optionLabelDisabled,
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={onClose}
          activeOpacity={0.7}
        >
          <Text style={styles.cancelButtonText}>キャンセル</Text>
        </TouchableOpacity>
      </View>
    </BottomSheet>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    paddingHorizontal: spacing.md,
  },
  titleContainer: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: {
    ...typography.body,
    fontWeight: '600',
    color: colors.textSecondary,
    textAlign: 'center',
  },
  optionsContainer: {
    paddingVertical: spacing.xs,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  optionDisabled: {
    opacity: 0.5,
  },
  icon: {
    marginRight: spacing.md,
  },
  optionLabel: {
    ...typography.bodyLarge,
    color: colors.text,
  },
  optionLabelDestructive: {
    color: colors.error,
  },
  optionLabelDisabled: {
    color: colors.textSecondary,
  },
  cancelButton: {
    marginTop: spacing.sm,
    paddingVertical: spacing.md,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cancelButtonText: {
    ...typography.bodyLarge,
    fontWeight: '600',
    color: colors.primary,
  },
});
